import { Injectable } from "@angular/core";
import { Observable, shareReplay, tap, EMPTY } from "rxjs";
import * as moment from "moment";
import { ApiService } from "./api.service";
import { Token } from "../models/Token.model";
import jwtDecode, { JwtPayload } from "jwt-decode";
import { User } from "../models/User.model";
import { environment } from "src/environments/environment";
import { Router } from "@angular/router";

@Injectable({ providedIn: "root" })
export class AuthService {
  constructor(private apiService: ApiService, private router: Router) {}

  login(email: string, password: string): Observable<Token> {
    return this.apiService.login({ email, password }).pipe(
      tap((res) => this.setSession(res)),
      // Carrega dados completos do usuário após login bem-sucedido
      tap(() => {
        // Pequeno delay para garantir que o token foi salvo
        setTimeout(() => {
          this.loadCurrentUserData().subscribe({
            error: (err) => console.warn("Failed to load user data:", err)
          });
        }, 100);
      }),
      shareReplay()
    );
  }

  refreshToken() {
    if (!environment.production) console.log("refreshing token...");

    var token = localStorage.getItem("token");
    var refreshToken = localStorage.getItem("refreshToken");

    if (!token || !refreshToken) {
      this.router.navigate(["login"]);
      return EMPTY;
    }

    return this.apiService.refreshToken({ token, refreshToken }).pipe(
      tap((res) => {
        this.setSession(res);
        if (!environment.production)
          console.log("successfully refreshed token");
      }),
      shareReplay()
    );
  }

  private setSession(authResponse: any) {
    // O backend otimizado retorna uma estrutura diferente
    let token: Token;
    
    if (authResponse.token) {
      // Nova estrutura do backend otimizado
      token = {
        token: authResponse.token,
        refreshToken: authResponse.refreshToken,
        expiration: authResponse.expiration
      };
    } else {
      // Estrutura antiga (fallback)
      token = authResponse as Token;
    }

    const user = this.getUser(token.token);
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("token", token.token);
    localStorage.setItem("refreshToken", token.refreshToken);
    localStorage.setItem("expiration", token.expiration);

    // Armazena dados extras do usuário se disponíveis
    if (authResponse.user) {
      localStorage.setItem("userProfile", JSON.stringify(authResponse.user));
    }
  }

  getUser(id_token: string): User {
    try {
      var decoded_token = jwtDecode<any>(id_token);
      
      if (!environment.production) {
        console.log("Decoded token:", decoded_token);
      }

      var roles = new Array<string>();

      // Primeiro, tenta extrair roles do novo formato
      const roleClaimKey = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";
      
      if (decoded_token[roleClaimKey]) {
        if (Array.isArray(decoded_token[roleClaimKey])) {
          decoded_token[roleClaimKey].forEach((role: string) => {
            roles.push(role);
          });
        } else {
          roles.push(decoded_token[roleClaimKey]);
        }
      }

      // Fallback: tenta extrair de outras possíveis localizações
      if (roles.length === 0 && decoded_token.role) {
        if (Array.isArray(decoded_token.role)) {
          roles = decoded_token.role;
        } else {
          roles.push(decoded_token.role);
        }
      }

      // Extrai informações do usuário
      const nameClaimKey = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name";
      const emailClaimKey = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress";
      
      let userName = decoded_token[nameClaimKey] || decoded_token.unique_name || decoded_token.name;
      let email = decoded_token[emailClaimKey] || decoded_token.email;

      // Se não conseguir extrair do token, tenta do localStorage
      if (!userName || !email) {
        const userProfile = localStorage.getItem("userProfile");
        if (userProfile) {
          const profile = JSON.parse(userProfile);
          userName = userName || profile.userName;
          email = email || profile.email;
        }
      }

      const user = new User(userName, email, roles);

      if (!environment.production) {
        console.log("Extracted user:", user);
        console.log("Roles found:", roles);
      }

      return user;
    } catch (error) {
      console.error("Error decoding token:", error);
      console.error("Token:", id_token);
      
      // Fallback: retorna usuário vazio
      return new User("", "", []);
    }
  }

  // Método para carregar dados completos do usuário atual
  loadCurrentUserData(): Observable<any> {
    return this.apiService.getCurrentUser().pipe(
      tap((userData) => {
        // Atualiza localStorage com dados completos
        localStorage.setItem("userProfile", JSON.stringify(userData));
        
        // Cria objeto User atualizado
        const user = new User(
          userData.userName,
          userData.email,
          userData.roles || []
        );
        localStorage.setItem("user", JSON.stringify(user));
        
        if (!environment.production) {
          console.log("User data loaded:", userData);
        }
      }),
      shareReplay(1)
    );
  }

  // Método para obter informações adicionais do usuário
  getUserProfile(): any {
    const userProfile = localStorage.getItem("userProfile");
    return userProfile ? JSON.parse(userProfile) : null;
  }

  // Método para verificar se o usuário tem uma role específica
  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user ? user.roles.includes(role) : false;
  }

  // Método para obter o usuário atual do localStorage
  getCurrentUser(): User | null {
    const userStr = localStorage.getItem("user");
    return userStr ? JSON.parse(userStr) : null;
  }

  // Métodos para acessar propriedades específicas do usuário
  getCurrentUserId(): string | null {
    const userProfile = this.getUserProfile();
    return userProfile?.id || null;
  }

  getCurrentUserCustomerId(): number | null {
    const userProfile = this.getUserProfile();
    return userProfile?.customerId || null;
  }

  getCurrentUserResponsavel(): string | null {
    const userProfile = this.getUserProfile();
    return userProfile?.responsavel || null;
  }

  getCurrentUserEmail(): string | null {
    const userProfile = this.getUserProfile();
    const currentUser = this.getCurrentUser();
    return userProfile?.email || currentUser?.email || null;
  }

  getCurrentUserName(): string | null {
    const userProfile = this.getUserProfile();
    const currentUser = this.getCurrentUser();
    return userProfile?.userName || currentUser?.username || null;
  }

  getCurrentUserRoles(): string[] {
    const userProfile = this.getUserProfile();
    const currentUser = this.getCurrentUser();
    return userProfile?.roles || currentUser?.roles || [];
  }

  // Método para obter todos os dados do usuário em um objeto
  getCurrentUserComplete(): any {
    const userProfile = this.getUserProfile();
    const currentUser = this.getCurrentUser();
    
    return {
      id: userProfile?.id || null,
      userName: userProfile?.userName || currentUser?.username || null,
      email: userProfile?.email || currentUser?.email || null,
      customerId: userProfile?.customerId || null,
      responsavel: userProfile?.responsavel || null,
      roles: userProfile?.roles || currentUser?.roles || []
    };
  }

  // Método para verificar se o token está próximo do vencimento
  isTokenExpiringSoon(minutesThreshold: number = 5): boolean {
    const expiration = this.getExpiration();
    const now = moment();
    const timeUntilExpiration = expiration.diff(now, 'minutes');
    
    return timeUntilExpiration <= minutesThreshold && timeUntilExpiration > 0;
  }

  logout() {
    this.apiService.logout().subscribe({
      next: (_res) => {
        if (!environment.production) console.log("successfully logged out");
      },
      error: (err) => {
        console.error("failed to logout", err);
      },
      complete: () => {
        this.clearSession();
      },
    });
  }

  // Método separado para limpar a sessão
  private clearSession() {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("expiration");
    localStorage.removeItem("userProfile");
  }

  // Logout local sem chamar a API
  logoutLocal() {
    this.clearSession();
    this.router.navigate(["login"]);
  }

  public isLoggedIn(): boolean {
    const token = localStorage.getItem("token");
    if (!token) return false;

    const expiration = this.getExpiration();
    const isValid = moment().isBefore(expiration);

    // Se o token expirou, limpa a sessão
    if (!isValid) {
      this.clearSession();
    }

    return isValid;
  }

  isLoggedOut(): boolean {
    return !this.isLoggedIn();
  }

  getExpiration() {
    const expiration = localStorage.getItem("expiration");
    if (expiration) {
      return moment(expiration);
    } else {
      return moment(0);
    }
  }

  // Método para debug - mostra informações do token
  debugToken(): void {
    if (!environment.production) {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const decoded = jwtDecode<any>(token);
          console.log("Token debug info:", {
            decoded: decoded,
            expiration: this.getExpiration().format(),
            isLoggedIn: this.isLoggedIn(),
            currentUser: this.getCurrentUser(),
            userProfile: this.getUserProfile()
          });
        } catch (error) {
          console.error("Error debugging token:", error);
        }
      } else {
        console.log("No token found");
      }
    }
  }
}